import FetchRetentionSettings from '@Application/Accounts/RetentionSettings/Queries/FetchRetentionSettings';
import fetchRetentionSettings from '@Application/Accounts/RetentionSettings/fetchRetentionSettings';
import ErrorState from '@Guidelines/Component/ErrorState';
import { theme } from '@Guidelines/Theme/theme';
import PurgeStatus from '@UI/Pages/Administration/Retention/PurgeStatus';
import RetentionSettingsForm from '@UI/Pages/Administration/Retention/RetentionSettingsForm';
import useStrictAuthenticatedUser from '@UI/Providers/AuthenticatedUserProvider/useStrictAuthenticatedUser';
import { css } from '@emotion/react';
import type * as React from 'react';
import { useDispatch, useStoreQuery } from '@verclam/synapse';
import { Skeleton } from '@verclam/ui-kit';

const cardStyle = css`
	display: flex;
	flex-direction: column;
	gap: 1.75rem;
	padding: 1.5rem;
	border-radius: ${theme.radii.xlarge};
	background-color: ${theme.colors.surface};
	border: 1.5px solid ${theme.colors.border};
`;

/** Loads the current account's retention durations and renders the form once they are known. */
const RetentionContent: React.FC = () => {
	const dispatch = useDispatch();
	const { user } = useStrictAuthenticatedUser();
	const accountId = user.account?.id ?? null;

	const {
		data: settings,
		error,
		refetch,
	} = useStoreQuery({
		selector: (state) => state.retentionSettingsStore.settings,
		stopOn: (value) => value !== null && value.accountId === accountId,
		query: () => {
			if (accountId === null) {
				return Promise.resolve();
			}

			return dispatch(fetchRetentionSettings(new FetchRetentionSettings({ accountId })));
		},
		deps: [accountId],
	});

	if (accountId === null) {
		return <ErrorState title="Aucun compte n'est rattaché à votre utilisateur." />;
	}

	if (error !== null) {
		return (
			<ErrorState
				title="Impossible de charger les durées de conservation"
				description="Une erreur est survenue pendant le chargement des durées."
				onRetry={refetch}
			/>
		);
	}

	if (settings === null || settings.accountId !== accountId) {
		return (
			<Skeleton height="16rem" borderRadius={theme.radii.xlarge} animation="wave" shape="rectangle" />
		);
	}

	return (
		<div css={cardStyle}>
			<RetentionSettingsForm settings={settings} />
			<PurgeStatus />
		</div>
	);
};

export default RetentionContent;
